import { ALL_LEAF_CATEGORIES, STYLE_FACETS, TOOL_ALIASES, TOOL_SUGGESTIONS } from '../../constants/tags';

type TagStateLike = {
    primaryCategory: string | null;
    secondaryCategories: string[];
    toolTags: string[];
    styleFacets: string[];
};

type TagSourceLike = Partial<TagStateLike> & {
    tags?: string[];
};

const LEGACY_CATEGORY_NAME_TO_ID: Record<string, string> = {
    ...Object.fromEntries(ALL_LEAF_CATEGORIES.map((category) => [category.name, category.id])),
    '宣传片/预告片': 'cat_brand_film',
    '动态Logo/片头': 'cat_dynamic_logo',
    '电商产品展示': 'cat_ecom_video',
    '概念设计/动态分镜': 'cat_character_design',
    'AI 角色设计': 'cat_character_design',
    'Prompt': 'cat_prompt_eng',
    'LoRA': 'cat_lora_training',
    'AI 培训': 'cat_ai_training',
    'ComfyUI 工作流定制': 'cat_comfy_workflow',
    '工作流自动化': 'cat_comfy_workflow',
    'AI 咨询服务': 'cat_copywriting',
    'AI 音乐 / 音效': 'cat_bgm',
    '语音合成 / TTS': 'cat_tts',
    'AI 剧本创作': 'cat_script',
};

const LEGACY_STYLE_NAME_TO_ID: Record<string, string> = {
    '赛博朋克': 'sf_cyberpunk',
    '日漫/二次元': 'sf_anime',
    '超写实/电影感': 'sf_cinematic',
    '油画风格': 'sf_realistic',
    '3D渲染/C4D': 'sf_realistic',
    '极简/冷淡风': 'sf_minimal',
    '像素风': 'sf_pixel',
    '水彩画': 'sf_watercolor',
    '复古/合成器波': 'sf_cinematic',
    '水墨/国画': 'sf_chinese',
    '黏土/定格动画': 'sf_realistic',
    '盲盒/Q版': 'sf_anime',
};

const LEAF_NAME_BY_ID = Object.fromEntries(ALL_LEAF_CATEGORIES.map((category) => [category.id, category.name]));
const STYLE_NAME_BY_ID = Object.fromEntries(STYLE_FACETS.map((facet) => [facet.id, facet.name]));
const KNOWN_TOOL_SET = new Set<string>([
    ...TOOL_SUGGESTIONS,
    ...Object.keys(TOOL_ALIASES),
    ...Object.values(TOOL_ALIASES).flat(),
].map((tag) => tag.toLowerCase().trim()));

export function isKnownToolTag(tag: string): boolean {
    return KNOWN_TOOL_SET.has(tag.toLowerCase().trim());
}

function hasStructuredTagData(data: TagSourceLike): boolean {
    return data.primaryCategory !== undefined
        || (data.secondaryCategories?.length ?? 0) > 0
        || (data.styleFacets?.length ?? 0) > 0
        || (data.toolTags?.length ?? 0) > 0;
}

function mergeUnique(primary: string[], fallback: string[]): string[] {
    return Array.from(new Set([...primary, ...fallback]));
}

function deriveLegacyTagState(tags: string[]): TagStateLike {
    if (tags.length === 0) {
        return {
            primaryCategory: null,
            secondaryCategories: [],
            toolTags: [],
            styleFacets: [],
        };
    }

    const matchedLeafIds: string[] = [];
    const matchedCategoryTags = new Set<string>();
    for (const tag of tags) {
        const leafId = LEGACY_CATEGORY_NAME_TO_ID[tag];
        if (!leafId) {
            continue;
        }
        if (!matchedLeafIds.includes(leafId)) {
            matchedLeafIds.push(leafId);
        }
        matchedCategoryTags.add(tag);
    }

    const styleFacets: string[] = [];
    const matchedStyleTags = new Set<string>();
    const seenFacets = new Set<string>();
    for (const tag of tags) {
        const facetId = LEGACY_STYLE_NAME_TO_ID[tag];
        if (!facetId) {
            continue;
        }
        if (!seenFacets.has(facetId)) {
            seenFacets.add(facetId);
            styleFacets.push(facetId);
        }
        matchedStyleTags.add(tag);
    }

    const toolTags: string[] = [];
    const matchedToolTags = new Set<string>();
    const seenTools = new Set<string>();
    for (const tag of tags) {
        if (!isKnownToolTag(tag) || seenTools.has(tag)) {
            continue;
        }
        seenTools.add(tag);
        matchedToolTags.add(tag);
        toolTags.push(tag);
    }

    const preservedLegacyTags: string[] = [];
    const seenPreservedTags = new Set(toolTags);
    for (const tag of tags) {
        if (matchedCategoryTags.has(tag) || matchedStyleTags.has(tag) || matchedToolTags.has(tag)) {
            continue;
        }
        if (seenPreservedTags.has(tag)) {
            continue;
        }
        seenPreservedTags.add(tag);
        preservedLegacyTags.push(tag);
    }

    return {
        primaryCategory: matchedLeafIds[0] || null,
        secondaryCategories: matchedLeafIds.slice(1, 3),
        toolTags: [...toolTags, ...preservedLegacyTags],
        styleFacets,
    };
}

export function deriveTagStateFromSource(data: TagSourceLike | undefined): TagStateLike {
    if (!data) {
        return {
            primaryCategory: null,
            secondaryCategories: [],
            toolTags: [],
            styleFacets: [],
        };
    }

    const legacyFallback = deriveLegacyTagState(data.tags || []);

    if (hasStructuredTagData(data)) {
        return {
            primaryCategory: data.primaryCategory ?? legacyFallback.primaryCategory,
            secondaryCategories: data.secondaryCategories?.length ? data.secondaryCategories : legacyFallback.secondaryCategories,
            styleFacets: data.styleFacets?.length ? mergeUnique(data.styleFacets, legacyFallback.styleFacets) : legacyFallback.styleFacets,
            toolTags: data.toolTags?.length ? mergeUnique(data.toolTags, legacyFallback.toolTags) : legacyFallback.toolTags,
        };
    }

    return legacyFallback;
}

export function flattenTagStateToNames(value: TagStateLike): string[] {
    return [
        ...(value.primaryCategory ? [LEAF_NAME_BY_ID[value.primaryCategory] || value.primaryCategory] : []),
        ...value.secondaryCategories.map((id) => LEAF_NAME_BY_ID[id] || id),
        ...value.toolTags,
        ...value.styleFacets.map((id) => STYLE_NAME_BY_ID[id] || id),
    ];
}
